import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  HStack,
  VStack,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import {
  getUncategorizedTransactions,
  resolveUncategorizedTransaction,
} from "../../api/uncategorized";

const UncategorizedTransactionModal = ({ isOpen, onClose, customerId, summary }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState({});
  const toast = useToast();

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getUncategorizedTransactions(customerId, {
        status: "active",
      });
      setTransactions(data.results || data);
    } catch (error) {
      console.error("Error fetching uncategorized transactions:", error);
      toast({
        title: "Error",
        description: "Failed to load uncategorized transactions",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, toast]);

  useEffect(() => {
    if (isOpen && customerId) {
      fetchTransactions();
    }
  }, [isOpen, customerId, fetchTransactions]);

  const handleResolveTransaction = async (transactionId) => {
    try {
      setResolving(prev => ({ ...prev, [transactionId]: true }));
      await resolveUncategorizedTransaction(customerId, transactionId);
      
      // Remove the resolved transaction from the list
      setTransactions(prev => prev.filter(t => t.id !== transactionId));
      
      toast({
        title: "Success",
        description: "Transaction resolved successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error resolving transaction:", error);
      toast({
        title: "Error",
        description: "Failed to resolve transaction",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResolving(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const getDaysOldColor = (daysOld) => {
    if (daysOld >= 60) return "red";
    if (daysOld >= 30) return "orange";
    return "green";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="start" spacing={2}>
            <Text>Uncategorized Transactions</Text>
            {summary && (
              <HStack spacing={4}>
                <Badge colorScheme="orange">
                  {summary.active_uncategorized_count} transactions
                </Badge>
                <Badge colorScheme="blue">
                  Total: {formatCurrency(summary.total_uncategorized_amount)}
                </Badge>
                {summary.old_transactions_count > 0 && (
                  <Badge colorScheme="red">
                    {summary.old_transactions_count} over 14 days old
                  </Badge>
                )}
              </HStack>
            )}
          </VStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" height="200px">
              <Spinner size="lg" />
            </Flex>
          ) : transactions.length === 0 ? (
            <Alert status="success">
              <AlertIcon />
              <AlertTitle>No uncategorized transactions!</AlertTitle>
              <AlertDescription>
                All transactions have been properly categorized.
              </AlertDescription>
            </Alert>
          ) : (
            <Box>
              <Alert status="info" marginBottom={4}>
                <AlertIcon />
                <AlertTitle>Action Required</AlertTitle>
                <AlertDescription>
                  These transactions need to be categorized to ensure accurate financial reporting. 
                  Please work with your bookkeeper to properly categorize these items.
                </AlertDescription>
              </Alert>
              
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Description</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Age</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transactions.map((transaction) => (
                    <Tr key={transaction.id}>
                      <Td>
                        {format(new Date(transaction.date), 'MMM dd, yyyy')}
                      </Td>
                      <Td>
                        <Text noOfLines={2} maxWidth="200px">
                          {transaction.description}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="bold">
                          {formatCurrency(transaction.amount)}
                        </Text>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={getDaysOldColor(transaction.days_old)}
                          variant="subtle"
                        >
                          {transaction.days_old} days
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          colorScheme="green"
                          isLoading={resolving[transaction.id]}
                          loadingText="Resolving"
                          onClick={() => handleResolveTransaction(transaction.id)}
                        >
                          Resolve
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UncategorizedTransactionModal; 